import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Truck, Shield, ArrowLeft, Plus, Minus, Check, Smartphone, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";

export default function ProductDetailPage() {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { add, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    api.get(`/products/${sku}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
    }).catch(() => toast.error("Produkt nicht gefunden"));
  }, [sku]);

  // De-duplicated image list, primary first
  const images = useMemo(() => {
    if (!product) return [];
    const list = [product.image, ...(Array.isArray(product.images) ? product.images : [])]
      .filter(Boolean)
      .filter((url, i, arr) => arr.indexOf(url) === i);
    return list.length ? list : ["/images/store/logo-official.png"];
  }, [product]);

  // Compatibility data (supports both new array fields + legacy string)
  const compatBrands = useMemo(() => {
    if (!product) return [];
    return Array.isArray(product.compatible_brands) ? product.compatible_brands.filter(Boolean) : [];
  }, [product]);
  const compatModels = useMemo(() => {
    if (!product) return [];
    return Array.isArray(product.compatible_models) ? product.compatible_models.filter(Boolean) : [];
  }, [product]);
  const compatLegacy = product?.compatible || "";
  const hasCompatData = compatBrands.length > 0 || compatModels.length > 0 || compatLegacy;

  const addToCart = () => {
    add(product, qty);
    setCartOpen(true);
  };

  const nextImg = () => setActiveImg((i) => (i + 1) % images.length);
  const prevImg = () => setActiveImg((i) => (i - 1 + images.length) % images.length);

  if (!product) {
    return <div className="pt-32 px-6 max-w-7xl mx-auto"><div className="h-96 bg-[#141417] rounded-3xl animate-pulse"></div></div>;
  }

  const hasMultiImg = images.length > 1;
  const soldOut = product.availability === "sold_out";

  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen" data-testid="product-detail">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Link to="/zubehoer" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Shop
        </Link>

        <div className="grid md:grid-cols-12 gap-10 lg:gap-12">
          {/* GALLERY */}
          <div className="md:col-span-7" data-testid="product-gallery">
            {/* Main image */}
            <div className="relative aspect-square rounded-3xl bg-[#141417] border border-white/5 overflow-hidden group">
              {images.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`${product.name} – Bild ${idx + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                    idx === activeImg ? "opacity-100" : "opacity-0"
                  }`}
                  loading={idx === 0 ? "eager" : "lazy"}
                  data-testid={`gallery-main-img-${idx}`}
                />
              ))}

              {/* Arrow controls (only if >1) */}
              {hasMultiImg && (
                <>
                  <button
                    type="button"
                    onClick={prevImg}
                    aria-label="Vorheriges Bild"
                    data-testid="gallery-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-md border border-white/15 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImg}
                    aria-label="Nächstes Bild"
                    data-testid="gallery-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-md border border-white/15 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  {/* Index badge */}
                  <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur text-[10px] text-white font-semibold tracking-wider border border-white/10">
                    {activeImg + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {hasMultiImg && (
              <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-2" data-testid="gallery-thumbs">
                {images.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImg(idx)}
                    data-testid={`gallery-thumb-${idx}`}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                      idx === activeImg
                        ? "border-[#DC1928] ring-2 ring-[#DC1928]/30"
                        : "border-white/5 hover:border-white/25 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="md:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
              {product.brand}
              {product.brand && compatLegacy ? " · " : ""}
              {compatLegacy}
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white mt-2 leading-tight">{product.name}</h1>

            <div className="flex items-center gap-2 mt-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i <= Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
                ))}
              </div>
              <span className="text-sm text-zinc-500">{product.rating} · Verifizierte Bewertungen</span>
            </div>

            <div className="mt-8">
              <div className="font-display text-5xl font-bold text-white numeric">€{product.price.toFixed(2)}</div>
              <div className="text-sm text-zinc-500 mt-1">inkl. 20% MwSt.</div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm">
              {soldOut ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  <span className="text-amber-300 font-medium" data-testid="product-sold-out">Ausverkauft</span>
                  <span className="text-zinc-500">· bald wieder lieferbar</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span className="text-emerald-400 font-medium">Auf Lager</span>
                  <span className="text-zinc-500">· sofort versandbereit</span>
                </>
              )}
            </div>

            <div className="mt-8 flex items-center gap-3">
              <div className="flex items-center border border-white/10 rounded-full bg-[#141417]">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-white" data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-medium text-white numeric" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center text-white" data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
              </div>
              <Button
                onClick={addToCart}
                disabled={soldOut}
                className="flex-1 bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 btn-lift glow-red-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:hover:bg-zinc-700 disabled:shadow-none"
                data-testid="add-to-cart-btn"
              >
                {soldOut ? "Aktuell ausverkauft" : `In den Warenkorb · €${(product.price * qty).toFixed(2)}`}
              </Button>
            </div>

            <div className="mt-6 p-4 rounded-2xl bg-[#141417] border border-white/5" data-testid="payment-methods">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Sichere Bezahlung</div>
              <div className="grid grid-cols-3 gap-2">
                <PayBadge label="Kreditkarte" />
                <PayBadge label="Apple Pay" />
                <PayBadge label="Google Pay" />
              </div>
            </div>

            <div className="mt-6 space-y-2 text-sm">
              <Feature icon={Truck} text="Versand in 24h innerhalb Wien" />
              <Feature icon={Shield} text="6 Monate Garantie" />
              <Feature icon={Check} text="14 Tage Rückgaberecht" />
            </div>
          </div>
        </div>

        {/* COMPATIBILITY */}
        {hasCompatData && (
          <section className="mt-20 p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#16161B] via-[#101015] to-[#0A0A0B] border border-white/8" data-testid="product-compatibility">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#DC1928]/12 border border-[#DC1928]/25 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-[#FF4F5E]" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF4F5E] font-bold">Kompatibilität</div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-1.5 leading-tight">
                  {compatModels.length > 0
                    ? <>Passend für <span className="text-zinc-500">{compatModels.length} Modell{compatModels.length === 1 ? "" : "e"}.</span></>
                    : <>Passend für <span className="text-zinc-500">{compatLegacy}.</span></>}
                </h2>

                {compatBrands.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2" data-testid="compat-brands">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold self-center mr-1">Marken</div>
                    {compatBrands.map((b) => (
                      <span
                        key={b}
                        className="px-3 h-8 inline-flex items-center rounded-full bg-white/8 border border-white/15 text-xs font-semibold text-white"
                        data-testid={`compat-brand-${b.toLowerCase()}`}
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                )}

                {compatModels.length > 0 && (
                  <div className="mt-5" data-testid="compat-models">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2.5">
                      Kompatible Modelle
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {compatModels.map((m) => (
                        <Link
                          key={m}
                          to={`/zubehoer?model=${encodeURIComponent(m)}`}
                          className="px-3 h-8 inline-flex items-center rounded-full bg-[#DC1928]/10 border border-[#DC1928]/30 text-xs font-medium text-white hover:bg-[#DC1928]/20 hover:border-[#DC1928]/55 transition-colors"
                          data-testid={`compat-model-${m.replace(/\s+/g, "-").toLowerCase()}`}
                          title={`Mehr Zubehör für ${m}`}
                        >
                          {m}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {compatLegacy && compatModels.length === 0 && (
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                    <span className="text-white font-medium">{compatLegacy}</span>
                  </p>
                )}

                <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
                  Nicht sicher, ob dein Modell dabei ist?{" "}
                  <Link to="/kontakt" className="text-[#FF4F5E] hover:underline">Frag uns direkt</Link> — wir
                  beraten dich persönlich.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Description block */}
        {product.description && (
          <div className="mt-16 max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-white mb-4">Beschreibung</h2>
            <p className="text-lg text-zinc-400 leading-relaxed whitespace-pre-line">{product.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PayBadge({ label }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-[#1C1C20] border border-white/10 text-center text-xs font-semibold text-zinc-300">
      {label}
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-zinc-400">
      <Icon className="w-4 h-4 text-emerald-400" /> {text}
    </div>
  );
}
